// src/pages/ForgotPasswordPage.jsx
// ============================================================
// Paso 1 de 3 de la recuperación de contraseña: pedir el mail.
//
// El backend responde igual exista o no la cuenta, a propósito, para no
// publicar un oráculo de "esta dirección está registrada en Fixo". Por eso
// esta pantalla avanza SIEMPRE que la llamada salga bien: decir acá "esa
// cuenta no existe" filtraría justo lo que el backend cuida.
//
// ⚠ SOBRE EL INTERCEPTOR DE 401 DE src/api/api.js
//
// Ese interceptor, ante CUALQUIER respuesta 401, borra el token de
// localStorage y hace `window.location.href = '/'`. En este flujo eso sería
// destructivo: el usuario está a mitad de recuperar el acceso y se caería al
// inicio sin explicación, perdiendo el código o el ticket que tenía en mano.
//
// Hoy NO pasa, y no por casualidad: los tres endpoints del flujo
// —/auth/password-reset/send, /verify y /confirm— devuelven 200, 400 o 500,
// nunca 401. Verificado contra routes/auth.js del backend.
//
// Qué pasaría si eso cambiara: si alguien hiciera que /verify devolviera 401
// para un código incorrecto —que es una elección de status razonable y por eso
// el riesgo es real—, el usuario sería expulsado al inicio en vez de ver
// "código inválido". El síntoma no se parecería a la causa: parecería un bug
// de sesión y no del status code.
//
// Si ese día llega, la salida es una instancia de axios sin el interceptor de
// respuesta para estos tres llamados, no tocar el interceptor global, que
// existe para las rutas autenticadas y ahí hace lo correcto.
// ============================================================

import { useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../api/api';
import AuthCard from '../components/AuthCard';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const enviar = async () => {
    const limpio = email.trim();

    if (!limpio || !limpio.includes('@')) {
      setError('Ingresá un email válido');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await api.post('/auth/password-reset/send', { email: limpio });
      navigate('/recuperar/codigo', { state: { email: limpio } });
    } catch (e) {
      // Un error acá es real —red caída, o el mail no salió— y no "la cuenta
      // no existe", que devuelve 200.
      setError(e.response?.data?.error || 'No pudimos enviar el código. Probá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      titulo="Recuperá tu contraseña"
      subtitulo="Poné el email de tu cuenta y te mandamos un código de 6 dígitos."
    >
      <TextField
        fullWidth
        placeholder="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') enviar(); }}
        disabled={loading}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 },
        }}
      />

      {error && (
        <Typography sx={{ fontSize: 13, color: '#f87171', textAlign: 'center', mb: 2 }}>
          {error}
        </Typography>
      )}

      <Button
        fullWidth
        onClick={enviar}
        disabled={loading}
        sx={{
          bgcolor: '#E7A432', color: '#163244', borderRadius: 2, py: 1.4,
          fontWeight: 700, fontSize: 15,
          '&:hover': { bgcolor: '#f5bc55' },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: '#163244' }} /> : 'Enviar código'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography
          component={RouterLink}
          to="/"
          sx={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}
        >
          Volver a <Box component="span" sx={{ color: '#E7A432', fontWeight: 600 }}>iniciar sesión</Box>
        </Typography>
      </Box>
    </AuthCard>
  );
}
